/**
 *   Utils which allow to control bidi-specific features: btd, stt, numeric shaping
 */

import React from 'react';
import isString from 'lodash/isString';

const DEFAULT_DIR = '';
const DEFAULT_BTD = '';
const DEFAULT_G11N = 'default';
const LTR = 'ltr';
const RTL = 'rtl';
const CONTEXTUAL = 'contextual';
const AUTO = 'auto';
const EUROPIAN = 0;
const INDIC = 1;
const LRE = '\u202A';
const RLE = '\u202B';
const PDF = '\u202C';
const LRM = '\u200E';
const RLM = '\u200F';
const REG_DIR = /[A-Za-z\u05d0-\u065f\u066a-\u06ef\u06fa-\u07ff\ufb1d-\ufdff\ufe70-\ufefc]/;
const REG_SH = /([0-9])|([\u0660-\u0669])|([\u0590-\u05FF\u0608\u060B\u060D\u061B-\u064A\u066D-\u066F\u0671-\u06D5\u06E5-\u06E6\u06EE-\u06EF\u06FA-\u06FF\u0750-\u077F\u08A0-\u08E3\u200F\u202B\u202E\u2067\uFB50-\uFD3D\uFD40-\uFDCF\uFDF0-\uFDFC\uFDFE-\uFDFF\uFE70-\uFEFE]+)|([^0-9\u0590-\u05FF\u0660-\u0669\u0608\u060B\u060D\u061B-\u064A\u066D-\u066F\u0671-\u06D5\u06E5-\u06E6\u06EE-\u06EF\u06FA-\u06FF\u0750-\u077F\u08A0-\u08E3\u200F\u202B\u202E\u2067\uFB50-\uFD3D\uFD40-\uFDCF\uFDF0-\uFDFC\uFDFE-\uFDFF\uFE70-\uFEFE\u0600-\u0607\u0609-\u060A\u060C\u060E-\u061A\u064B-\u066C\u0670\u06D6-\u06E4\u06E7-\u06ED\u06F0-\u06F9\u08E4-\u08FF\uFD3E-\uFD3F\uFDD0-\uFDEF\uFDFD\uFEFF\u0000-\u0040\u005B-\u0060\u007B-\u007F\u0080-\u00A9\u00AB-\u00B4\u00B6-\u00B9\u00BB-\u00BF\u00D7\u00F7\u02B9-\u02BA\u02C2-\u02CF\u02D2-\u02DF\u02E5-\u02ED\u02EF-\u02FF\u2070\u2074-\u207E\u2080-\u208E\u2100-\u2101\u2103-\u2106\u2108-\u2109\u2114\u2116-\u2118\u211E-\u2123\u2125\u2127\u2129\u212E\u213A-\u213B\u2140-\u2144\u214A-\u214D\u2150-\u215F\u2189\uA720-\uA721\uA788\uFF01-\uFF20\uFF3B-\uFF40\uFF5B-\uFF65\uFFE0-\uFFE6\uFFE8-\uFFEE]+)/g;

function hasArabicChar(text, after) {
	if (!text) {
		return false;
	}
	let result = '';
	if (after) {
		const ind = text.indexOf(after);
		if (ind > 0 && ind < text.length - 1) {
			result = text.substring(ind + 1);
		}
	}
	const fdc = /[\u0600-\u065f\u066a-\u06ef\u06fa-\u07ff\ufb1d-\ufdff\ufe70-\ufefc]/.exec(result);

	return !!fdc;
}

class TextSegment {
	constructor(text, flowDir, segDir, isSeparator = false) {
		this.text = text;
		const guiDir = Bidi.isMirroredUI() ? RTL : LTR;
		this.flowDir = flowDir === DEFAULT_DIR ? guiDir : flowDir;
		this.segDir = segDir === DEFAULT_DIR ? this.flowDir : segDir;
		this.isSeparator = isSeparator;
	}
}

export default class Bidi {
	constructor(...props) {
		this._props = props;
	}

	static get AS_STRING() {
		return 1;
	}

	static get AS_HTML() {
		return 2;
	}

	static get AS_JSX() {
		return 3;
	}

	static get DIR_DEFAULT() {
		return DEFAULT_DIR;
	}

	static get DIR_LTR() {
		return LTR;
	}

	static get DIR_RTL() {
		return RTL;
	}

	static get DIR_CONTEXTUAL() {
		return CONTEXTUAL;
	}

	static get DIR_AUTO() {
		return AUTO;
	}

	static get FREETEXT() {
		return '0';
	}

	static get BREADCRUMB() {
		return '1';
	}

	static get COMMA() {
		return '2';
	}

	static get EMAIL() {
		return '3';
	}

	static get FILEPATH() {
		return '4';
	}

	static get FORMULA() {
		return '5';
	}

	static get UNDERSCORE() {
		return '6';
	}

	static get URL() {
		return '7';
	}

	static get WORD() {
		return '8';
	}

	static get FILENAME() {
		return '9';
	}

	static get CUSTOM() {
		return '99';
	}

	static getTextDir(btd) {
		const _btd = btd || Bidi._getTextDir();
		if (!Bidi.isBidi() || _btd === DEFAULT_BTD || _btd === DEFAULT_G11N) {
			return DEFAULT_BTD;
		} else if (_btd !== CONTEXTUAL && _btd !== AUTO) {
			return _btd;
		}

		return AUTO;
	}

	static getDir(dir) {
		const _dir = dir || Bidi._getDir();
		if (!Bidi.isBidi() || _dir === DEFAULT_DIR || _dir === DEFAULT_G11N) {
			return DEFAULT_DIR;
		} else if (_dir !== CONTEXTUAL && _dir !== AUTO) {
			return _dir;
		}

		return AUTO;
	}

	static resolveDir(txt, dir) {
		let text = txt;
		if (typeof text === 'number') {
			text = `${text}`;
		}
		const _dir = dir || Bidi.getDir();
		if (
			text === null ||
			text === undefined ||
			!Bidi.isBidi() ||
			dir === DEFAULT_DIR ||
			dir === DEFAULT_G11N
		) {
			return DEFAULT_DIR;
		} else if (dir !== CONTEXTUAL && dir !== AUTO) {
			return dir;
		}
		const fdc = REG_DIR.exec(text);
		if (fdc) {
			return fdc[0] <= 'z' ? LTR : RTL;
		}

		return Bidi.isMirroredUI() ? RTL : LTR;
	}

	static resolveTextDir(txt, pageTextDir, textDir) {
		let text = txt;
		if (typeof text === 'number') {
			text = `${text}`;
		}
		const btd = textDir || pageTextDir || Bidi.getTextDir();
		if (
			text === null ||
			text === undefined ||
			!Bidi.isBidi() ||
			btd === DEFAULT_DIR ||
			btd === DEFAULT_G11N
		) {
			return DEFAULT_DIR;
		} else if (btd !== CONTEXTUAL && btd !== AUTO) {
			return btd;
		}
		const fdc = REG_DIR.exec(text);
		if (fdc) {
			return fdc[0] <= 'z' ? LTR : RTL;
		}

		return Bidi.isMirroredUI() ? AUTO : LTR;
	}

	static resolveNumShape(pageNumShape, numShape) {
		const ns = numShape || pageNumShape || Bidi.getNumShape();
		if (typeof ns === 'string') {
			return String(ns) == 'true';
		}

		return ns;
	}

	static enforceDir(txt, outType, dir, reshape = false, startKey = 0) {
		let text = txt;
		if (typeof text === 'number') {
			text = `${text}`;
		}
		const btd = Bidi.resolveDir(text, dir);
		if (!Bidi.isBidi() || !text || btd === DEFAULT_DIR) {
			return Bidi.reshape(text, reshape);
		} else if (outType === Bidi.AS_STRING) {
			return (btd === RTL ? RLE : LRE) + Bidi.reshape(text, reshape) + PDF;
		} else if (outType === Bidi.AS_HTML) {
			return `<bdi dir='${btd}'>${Bidi.reshape(text, reshape)}</bdi>`;
		}

		return (
			<bdi key={`${startKey}`} dir={btd}>
				{Bidi.reshape(text, reshape)}
			</bdi>
		);
	}

	static reshape(txt, needReshape = true, needUnshape = false) {
		let text = txt;
		if (typeof text === 'number') {
			text = `${text}`;
		}
		if (!text || (!Bidi.isBidi() && !needUnshape) || (!needReshape && !needUnshape)) {
			return text;
		}
		const userLang = Bidi.getUserLanguage();
		const browserLang = window.navigator.languages
			? window.navigator.languages[0]
			: window.navigator.language || window.navigator.userLanguage;
		let nsType = userLang.substring(0, 2).toLowerCase() === 'ar' ? INDIC : EUROPIAN;
		let country = '';
		if (userLang.length > 2) {
			country = userLang.substring(3);
		} else if (browserLang.length > 2) {
			country = browserLang.substring(3);
		}
		const useLatin = ['DZ', 'EH', 'LY', 'MA', 'TN'].some(
			item => item === country.toUpperCase()
		);
		if (useLatin) {
			nsType = EUROPIAN;
		}
		if (needUnshape) {
			nsType = EUROPIAN;
		}
		const result = text.replace(REG_SH, (curChs, latNum, araNum) => {
			if (araNum) {
				if (nsType === EUROPIAN) {
					return araNum.charCodeAt(0) - 1632;
				}

				return araNum;
			} else if (latNum) {
				if (nsType === INDIC) {
					return String.fromCharCode(parseInt(latNum, 10) + 1632);
				}

				return latNum;
			}

			return curChs;
		});

		return result;
	}

	static isolate(text, outType, dir, startKey = 0) {
		const outDir = Bidi.isMirroredUI() ? RTL : LTR;
		const guiDir = dir || outDir;
		if (!text || !Bidi.isBidi()) {
			return text;
		} else if (outType === Bidi.AS_STRING) {
			const mark = guiDir === RTL ? RLM : LRM;

			return mark + text + mark;
		} else if (outType === Bidi.AS_HTML) {
			return `<span style="direction: ${guiDir}; unicode-bidi: embed;"></span>${text}<span style="direction: ${guiDir}; unicode-bidi: embed;"></span>`;
		}
		const spStyle = {
			direction: guiDir,
			unicodeBidi: 'embed',
		};
		const result = [];
		result.push(<span key={startKey * 100 + 1} style={spStyle} />);
		result.push(<span key={startKey * 100 + 2}>{text}</span>);
		result.push(<span key={startKey * 100 + 3} style={spStyle} />);

		return result;
	}

	static parse(text, sttType, delimiters, flowDir, segDir, findFromEnd, stopIn) {
		const results = [new TextSegment(text, flowDir, segDir)];
		const dels = isString(delimiters) ? delimiters.split('') : delimiters;
		let count = 0;
		let ind = 0;
		let offset = 0;
		let curDel = '';
		let point = '';
		while (true) {
			const actInd = findFromEnd ? results.length - ind - 1 : ind;
			if (results[actInd].isSeparator) {
				ind++;
				if (ind === results.length) {
					break;
				} else {
					continue;
				}
			}
			const curText = results[actInd].text;
			curDel = '';
			offset = findFromEnd ? -1 : curText.length;
			let found = false;
			let i = 0;
			for (i = 0; i < dels.length; i++) {
				point = dels[i];
				const delOffset = findFromEnd ? curText.lastIndexOf(point) : curText.indexOf(point);
				if (delOffset < 0) {
					continue;
				} else if (
					(findFromEnd && delOffset > offset) ||
					(!findFromEnd && delOffset < offset)
				) {
					offset = delOffset;
					curDel = point;
					found = true;
				}
			}
			if (found) {
				results.splice(actInd + 1, 0, new TextSegment(curDel, flowDir, flowDir, true));
				if (curText.length > offset + curDel.length) {
					results.splice(
						actInd + 2,
						0,
						new TextSegment(curText.substring(offset + curDel.length), flowDir, segDir)
					);
				}
				results[actInd].text = curText.substring(0, offset);
				count++;
				if (stopIn > 0 && count === stopIn) {
					break;
				}
			}
			ind++;
			if (ind === results.length) {
				break;
			} else {
				continue;
			}
		}

		return results;
	}

	static handleSTT(
		text,
		outType,
		sttType,
		pdelimiters = '',
		pflowDir = DEFAULT_DIR,
		psegDir = DEFAULT_DIR,
		pfindFromEnd = false,
		pstopIn = 0
	) {
		if (!text || !Bidi.isBidi() || sttType === Bidi.FREETEXT) {
			return text;
		}
		let delimiters = pdelimiters;
		let flowDir = pflowDir;
		const segDir = psegDir;
		let findFromEnd = pfindFromEnd;
		let stopIn = pstopIn;
		const guiDir = Bidi.isMirroredUI() ? RTL : LTR;
		switch (sttType) {
			case Bidi.EMAIL:
				delimiters = '<>.:,;@';
				flowDir = Bidi.getUserLanguage() === 'ar' && hasArabicChar(text, '@') ? RTL : LTR;
				break;
			case Bidi.FILEPATH:
				delimiters = '/\\:.';
				flowDir = LTR;
				break;
			case Bidi.FORMULA:
				delimiters = ' /%^&[]<>=!?~:.,|()+-*{}';
				flowDir = LTR;
				break;
			case Bidi.URL:
				delimiters = ':?#/@.[]=&';
				flowDir = LTR;
				break;
			case Bidi.BREADCRUMB:
				flowDir = flowDir === DEFAULT_DIR ? guiDir : flowDir;
				if (!delimiters) {
					delimiters = flowDir === RTL ? [' < ', ' | '] : [' > ', ' | '];
				}
				break;
			case Bidi.WORD:
				delimiters = ' ,.!?;:';
				break;
			case Bidi.COMMA:
				delimiters = ',';
				break;
			case Bidi.UNDERSCORE:
				delimiters = '_';
				break;
			case Bidi.FILENAME:
				delimiters = '.';
				flowDir = LTR;
				findFromEnd = true;
				stopIn = 1;
				break;
			case Bidi.CUSTOM:
				if (!delimiters) {
					return text;
				}
				break;
			default:
				return text;
		}
		const segments = Bidi.parse(
			text,
			sttType,
			delimiters,
			flowDir,
			segDir,
			findFromEnd,
			stopIn
		);
		const results = [];
		let i = 0;
		for (i = 0; i < segments.length; i++) {
			const segment = segments[i];
			let txt = segment.text;
			const actDir =
				segment.flowDir !== segment.segDir
					? Bidi.resolveDir(txt, segment.segDir)
					: segment.flowDir;
			if (!segment.isSeparator) {
				if (actDir !== segment.flowDir) {
					txt = Bidi.enforceDir(txt, outType, actDir, false, i);
				}
			} else {
				txt = Bidi.isolate(txt, outType, segment.flowDir, i);
			}
			results.push(txt);
		}
		if (outType !== Bidi.AS_JSX) {
			let res = results.join('');
			if (outType === Bidi.AS_STRING) {
				res = (Bidi.resolveDir(res, flowDir) === RTL ? RLE : LRE) + res + PDF;
			} else {
				res = `<bdi dir='${flowDir}'>${res}</bdi>`;
			}

			return res;
		}

		return <bdi dir={flowDir}>{results}</bdi>;
	}

	static removeUCC(text) {
		if (!text) {
			return text;
		}

		return text.replace(/(['\u202A' | '\u202B' | '\u202C' | '\u200E' | '\u200F'])/g, '');
	}

	static isBidiMark(text) {
		if (!text) {
			return false;
		}

		return ['\u202A', '\u202B', '\u202C', '\u200E', '\u200F'].some(
			item => item === text.charAt(0)
		);
	}

	static checkDels(event) {
		const key = event.keyCode || event.charCode;
		if (key !== 8 && key !== 46) {
			return;
		}
		const text = event.target.value;
		let start = event.target.selectionStart;
		const end = event.target.selectionEnd;
		let as = start;
		const offs = key === 8 ? -1 : 1;
		if (start !== end) {
			return;
		}
		if (key === 8) {
			// backsp
			as -= 1;
		}

		while (as >= 0 && Bidi.isBidiMark(text.charAt(as))) {
			start += offs;
			as += offs;
		}
		event.target.selectionStart = start;
		event.target.selectionEnd = start;
	}

	static getDefBidi() {
		return false;
	}

	static isBidi() {
		if (Bidi.hasInitParams()) {
			return Bidi.getCustBidi();
		} else {
			return Bidi.getDefBidi();
		}
	}
	static getCustBidi() {
		return Bidi.custBidi;
	}

	static getCustBTD() {
		return Bidi.custBTD;
	}

	static getDefDir() {
		if (Bidi.isMirroredUI()) {
			return 'rtl';
		} else {
			return 'ltr';
		}
	}

	static getDefBTD() {
		if (Bidi.isMirroredUI()) {
			return 'auto';
		} else {
			return '';
		}
	}

	static _getTextDir() {
		if (Bidi.hasInitParams()) {
			return Bidi.getCustBTD();
		} else {
			return Bidi.getDefBTD();
		}
	}

	static getNumShape() {
		if (Bidi.custNumShape) {
			return Bidi.custNumShape;
		} else {
			return Bidi.getDefNumShape();
		}
	}

	static getDefNumShape() {
		if (Bidi.getUserLanguage() === 'ar') {
			return true;
		}

		return false;
		if (Bidi.isMirroredUI()) {
			return 'auto';
		} else {
			return '';
		}
	}

	static hasInitParams() {
		if (typeof Bidi.custBTD !== 'undefined' && typeof Bidi.custBidi !== 'undefined') {
			return true;
		} else {
			return false;
		}
	}

	static initParams(bidi, btd, lang, num_shape) {
		Bidi.custBTD = btd;
		Bidi.custBidi = bidi;
		Bidi.custLang = lang;
		if (num_shape && Bidi.getUserLanguage() === 'ar') {
			Bidi.custNumShape = num_shape;
		} else {
			Bidi.custNumShape = false;
		}
	}

	static _getDir() {
		if (Bidi.getCustDir) {
			return Bidi.getCustDir();
		} else {
			return Bidi.getDefDir();
		}
	}

	static getDefLang() {
		const html = document.documentElement;
		if (html.lang) {
			return html.lang;
		} else {
			return window.navigator.languages
				? window.navigator.languages[0]
				: window.navigator.language || window.navigator.userLanguage;
		}
	}

	static getUserLanguage() {
		if (Bidi.custLang) {
			return Bidi.custLang;
		} else {
			return Bidi.getDefLang();
		}
	}

	static isMirroredUI() {
		const html = document.documentElement;
		if (window.getComputedStyle) {
			return window.getComputedStyle(html).direction === 'rtl';
		} else if (html.dir) {
			return html.dir === 'rtl';
		} else {
			return false;
		}
	}
	static getBidiProps() {
		return ['dir', 'pagedir', 'pageTextDir', 'textDir', 'numShape', 'pageNumShape'];
	}
}
